import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

import {
  Form,
  ICONS,
  PLATFORM_THEME_COLORS,
  Icon,
  Button,
  DeleteModal,
  NumberInput,
} from '@thinknet-gmbh/platform-common-ui';

import DatevClientsSelect from './components/datev-clients-select/datev-clients-select.component';


import { useDeleteHealthInsuranceNumberById } from 'src/hooks/health-insurance/delete-health-insurance-number-by-id.hook';

import {
  HealthInsuranceNumberWrapper,
  FormItemsWrapper,
  FormListWrapper,
} from './health-insurance-numbers-form-list.styles';

interface IDatevNumber {
  _id?: string;
  company?: string;
  location?: string;
  datevNumber?: string;
}

const HealthInsuranceNumbersFormList = ({
  form,
  datevNumbers,
}: {
  form: FormInstance<any>;
  datevNumbers: IDatevNumber[];
}) => {
  const { t } = useTranslation(['settings', 'employee']);
  const [healthInsuranceNumberToDelete, setHealthInsuranceNumberToDelete] = useState<any>(null);
  const [, setClientNumberSelected] = useState<boolean>(false);

  const deleteHealthInsuranceNumberById = useDeleteHealthInsuranceNumberById({
    onSuccess: () => {},
  });

  const onSelectClientNumber = () => {
    setClientNumberSelected(prev => !prev);
  };

  return (
    <HealthInsuranceNumberWrapper>
      <Form.List name={'healthInsuranceNumbers'}>
        {(fields, { add, remove }) => {
          const deleteHealthInsuranceNumberHandler = (fieldName: number) => {
            const pathToFormData = ['healthInsuranceNumbers', fieldName];
            const healthInsuranceNumberData = form.getFieldValue(pathToFormData);

            if (healthInsuranceNumberData?._id) {
              setHealthInsuranceNumberToDelete({ healthInsuranceNumberData, fieldName });
            } else {
              remove(fieldName);
            }
          };

          const onSubmitHealthInsuranceNumberDelete = () => {
            deleteHealthInsuranceNumberById({
              variables: { id: healthInsuranceNumberToDelete?.healthInsuranceNumberData?._id },
              onCompleted: data => {
                if (data) {
                  remove(healthInsuranceNumberToDelete?.fieldName);
                  setHealthInsuranceNumberToDelete(null);
                }
              },
            });
          };

          const getDatevNumbersOptions = ({
            fieldName,
            datevNumbers,
          }: {
            fieldName: number;
            datevNumbers: IDatevNumber[];
          }) => {
            const healthInsuranceNumbers = form.getFieldValue('healthInsuranceNumbers') || [];
            const dataToCheck = [...healthInsuranceNumbers];
            dataToCheck.length && dataToCheck.splice(fieldName, 1);
            const isClientNumberSelectedBefore = (value?: string) =>
              dataToCheck.length
                ? !!dataToCheck?.find(el => el?.clientNumbers?.includes(value))
                : false;
            return datevNumbers.map(item => {
              return {
                label: item.datevNumber || '',
                value: item._id || '',
                disabled: isClientNumberSelectedBefore(item._id),
              };
            });
          };

          return (
            <FormListWrapper>
              <DeleteModal
                title={t('settings:healthInsurance.deleteModalNumber.title')}
                subTitle={t('settings:healthInsurance.deleteModalNumber.subtitle', {
                  healthInsuranceName:
                    healthInsuranceNumberToDelete?.healthInsuranceNumberData?.healthInsuranceNumber,
                })}
                onSubmit={() => onSubmitHealthInsuranceNumberDelete()}
                submitBtnText={t('common:buttons.delete')}
                translate={t}
                onClose={() => setHealthInsuranceNumberToDelete(null)}
                isOpened={!!healthInsuranceNumberToDelete}
              />

              {fields.map(field => {
                const datevNumbersOptions = getDatevNumbersOptions({
                  fieldName: field?.name,
                  datevNumbers: datevNumbers,
                });

                return (
                  <FormItemsWrapper key={field.key} isVisibleDelete={fields.length > 1}>
                    <Form.Item
                      name={[field.name, 'healthInsuranceNumber']}
                      label={t('settings:healthInsurance.labels.number')}
                      dependencies={[['healthInsuranceNumbers', field.name, 'clientNumbers']]}
                      rules={[
                        {
                          type: 'number',
                          min: 1,
                          max: 999,
                          message: t('common:validation.error.mustBeBetween', {
                            value: '1-999',
                          }),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const clientNumbers = getFieldValue([
                              'healthInsuranceNumbers',
                              field.name,
                              'clientNumbers',
                            ]);
                            if (clientNumbers?.length && !value) {
                              return Promise.reject(
                                new Error(
                                  t('settings:healthInsurance.validation.numberAndClientBothRequired')
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <NumberInput
                        allowNegative={false}
                        thousandSeparator={false}
                        allowLeadingZeros={false}
                        placeholder="273"
                        maxLength={3}
                        disabled={false}
                      />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, 'clientNumbers']}
                      label={t('settings:salaryTypes.labels.clients')}
                      dependencies={[['healthInsuranceNumbers', field.name, 'healthInsuranceNumber']]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const healthInsuranceNumber = getFieldValue([
                              'healthInsuranceNumbers',
                              field.name,
                              'healthInsuranceNumber',
                            ]);
                            if (healthInsuranceNumber && !value?.length) {
                              return Promise.reject(
                                new Error(
                                  t('settings:healthInsurance.validation.numberAndClientBothRequired')
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatevClientsSelect
                        placeholder="Select from the list"
                        optionData={datevNumbersOptions}
                        onSelectClientNumber={onSelectClientNumber}
                      />
                    </Form.Item>

                    {fields.length > 1 && (
                      <Icon
                        icon={ICONS.trash}
                        color={PLATFORM_THEME_COLORS.tone.red}
                        size={22}
                        className={'remove-icon'}
                        rightMargin={false}
                        leftMargin={false}
                        onClick={() => deleteHealthInsuranceNumberHandler(field.name)}
                      />
                    )}
                  </FormItemsWrapper>
                );
              })}
              <Button
                type="text"
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => add({})}
              >
                {t('settings:healthInsurance.labels.addNumber')}
              </Button>
            </FormListWrapper>
          );
        }}
      </Form.List>
    </HealthInsuranceNumberWrapper>
  );
};

export default HealthInsuranceNumbersFormList;
